import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarCheck, Users, BarChart2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface SchedulePerformanceCardProps {
  dateRange?: { from: Date; to: Date };
}

export function SchedulePerformanceCard({ dateRange }: SchedulePerformanceCardProps) {
  // Query enrollments for total, canceled, and unique students
  const { data: stats } = useQuery({
    queryKey: ["schedulePerformance", dateRange],
    queryFn: async () => {
      let query = supabase
        .from("enrollments")
        .select("student_id, status, created_at");
      if (dateRange) {
        query = query.gte("created_at", dateRange.from.toISOString()).lte("created_at", dateRange.to.toISOString());
      }
      const { data, error } = await query;
      if (error) throw error;
      const totalEnrollments = (data || []).length;
      const canceledEnrollments = (data || []).filter(e => e.status === "canceled" || e.status === "withdrawn").length;
      const uniqueStudents = Array.from(new Set((data || []).map(e => e.student_id))).length;
      return { totalEnrollments, canceledEnrollments, uniqueStudents };
    },
  });

  return (
    <Card className="bg-gradient-to-br from-white via-blue-50 to-blue-100/60 shadow border-0 backdrop-blur-md">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Schedule Performance</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <CalendarCheck className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-2xl font-bold text-blue-900">{stats ? stats.totalEnrollments : "-"}</div>
            </div>
            <div className="text-sm text-muted-foreground">Total Enrollments</div>
            <div className="text-xs text-muted-foreground">
              Aggregated booked spots across all classes
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <BarChart2 className="h-5 w-5 text-red-600" />
              </div>
              <div className="text-2xl font-bold text-red-700">{stats ? stats.canceledEnrollments : "-"}</div>
            </div>
            <div className="text-sm text-muted-foreground">Canceled Enrollments</div>
            <div className="text-xs text-muted-foreground">
              Total canceled or transferred enrollments
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-2xl font-bold text-green-900">{stats ? stats.uniqueStudents : "-"}</div>
            </div>
            <div className="text-sm text-muted-foreground">Unique Students</div>
            <div className="text-xs text-muted-foreground">
              Distinct students enrolled in classes
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
